import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import homepageTranslations from '@/lib/homepageTranslations.json';

interface ProductPreviewSectionProps {
  currentLang?: 'sl' | 'en';
}

export default function ProductPreviewSection({ currentLang = 'sl' }: ProductPreviewSectionProps) {
  const lang = currentLang === 'en' ? 'en' : 'sl';
  const content = {
    label: homepageTranslations.productsPreview.label[lang],
    title: homepageTranslations.productsPreview.title[lang],
  };

  const categories = [
    {
      id: 'category_honey',
      name: homepageTranslations.productsPreview.categories.honey.name[lang],
      count: homepageTranslations.productsPreview.categories.honey.count[lang],
      image: 'https://images.unsplash.com/photo-1508867378473-37e364d3a385',
      alt: 'Glass jars filled with golden honey on wooden table with honeycomb',
    },
    {
      id: 'category_pollen',
      name: homepageTranslations.productsPreview.categories.pollen.name[lang],
      count: homepageTranslations.productsPreview.categories.pollen.count[lang],
      image: 'https://images.unsplash.com/photo-1624858857683-01f5dd02a251',
      alt: 'Colorful bee pollen granules in wooden spoon and bowl',
    },
    {
      id: 'category_propolis',
      name: homepageTranslations.productsPreview.categories.propolis.name[lang],
      count: homepageTranslations.productsPreview.categories.propolis.count[lang],
      image: 'https://images.unsplash.com/photo-1616987381923-7b5118f26a6d',
      alt: 'Elegant bottles of honey liqueur with amber liquid on dark background',
    },
    {
      id: 'category_wax',
      name: homepageTranslations.productsPreview.categories.wax.name[lang],
      count: homepageTranslations.productsPreview.categories.wax.count[lang],
      image: 'https://images.unsplash.com/photo-1729260551369-8a0f63a28b4a',
      alt: 'Natural beeswax candles and propolis products on rustic wooden surface',
    },
  ];

  return (
    <section className="py-32 px-6 md:px-10 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4">
          {content.label}
        </p>
        <h2 className="text-5xl md:text-6xl font-bold tracking-tighter">{content.title}</h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {categories.map((category) => (
          <Link
            key={category.id}
            href="/products"
            className="group relative aspect-[4/5] card-rounded overflow-hidden bg-muted"
          >
            <AppImage
              src={category.image}
              alt={category.alt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
              <p className="text-sm opacity-80">{category.count}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
